import React, { useState, useEffect } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Flag } from 'lucide-react';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsForm from '../core/HeadersAndFooters/HeaderButtonsForm';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import ConfirmationModal from '../core/ConfirmationModal';
import ReportModal from '../shared/ReportModal';
import useMediaQuery from '../../hooks/useMediaQuery';
import { userActionService } from '../../data/dataService';
import { useTranslation } from '../../i18n/useTranslation';
import { isRateLimited, rateLimitMessage, RATE_LIMIT_TITLE_KEY } from '../../utils/rateLimitMessage';

const ChatDetail = ({
  chat, // Original chat chat
  responses = [], // Array of response chats
  onBack,
  currentContext,
  selectedGroup,
  homeLocalityName,
  onPostResponse,
  userId
}) => {
  const theme = useTheme();
  const { isMobile, headerHeight, footerHeight } = useMediaQuery('detail');
  const { requireAuth, isLoggedIn } = useAuth();
  const { t, formattingLocale } = useTranslation('chat');

  // Build context object for header display
  const headerContext = currentContext === 'groups' && selectedGroup
    ? { name: selectedGroup.name, type: 'group' }
    : { name: homeLocalityName || 'Locality', type: 'locality' };
  const [activeResponseBox, setActiveResponseBox] = useState(null); // Track which chat has response box open
  const [responseText, setResponseText] = useState('');

  // Logo click listener - navigate back to MainFeed
  useEffect(() => {
    const handleLogoClick = () => {
      if (onBack) onBack();
    };
    window.addEventListener('ournet:context-switch', handleLogoClick);
    return () => window.removeEventListener('ournet:context-switch', handleLogoClick);
  }, [onBack]);

  // Auth check handler for response inputs
  const handleResponseInputFocus = (e) => {
    if (!isLoggedIn) {
      e.target.blur();
      requireAuth('gate_body_comment');
    }
  };

  // Like/Dislike state - tracks status for all messages by ID
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userLikeStatuses, setUserLikeStatuses] = useState({});
  const [messageCounts, setMessageCounts] = useState({}); // Track like/dislike counts for each message

  // Modal state
  const [modal, setModal] = useState({ visible: false, message: '', onOk: null });
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportTargetId, setReportTargetId] = useState(null);

  // Expand/collapse state - tracks which responses have their children visible
  const [expandedResponses, setExpandedResponses] = useState(new Set());

  const toggleExpanded = (chatId) => {
    setExpandedResponses(prev => {
      const next = new Set(prev);
      if (next.has(chatId)) {
        next.delete(chatId);
      } else {
        next.add(chatId);
      }
      return next;
    });
  };

  // Load user ID and like/dislike statuses when chat/responses load
  useEffect(() => {
    const loadUserAndLikeStatuses = async () => {
      if (!chat?.id) {
        return;
      }

      try {
        if (userId) {
          setCurrentUserId(userId);

          // Collect all message IDs (original chat + all responses)
          const allMessageIds = [chat.id, ...responses.map(r => r.id)];

          // Track views for all messages (fire-and-forget)
          allMessageIds.forEach(messageId => {
            userActionService.trackMessageEngagement(userId, messageId, 'view').catch(() => {});
          });

          // Load like statuses for all messages
          const statuses = {};
          await Promise.all(
            allMessageIds.map(async (messageId) => {
              const likeStatus = await userActionService.getMessageLikeDislike(userId, messageId);
              statuses[messageId] = likeStatus;
            })
          );

          setUserLikeStatuses(statuses);

          // Load real-time counts from user_actions (not stale database counts)
          const counts = {};
          await Promise.all(
            allMessageIds.map(async (messageId) => {
              const messageCounts = await userActionService.getMessageCounts(messageId);
              counts[messageId] = messageCounts;
            })
          );
          setMessageCounts(counts);
        }
      } catch (error) {
        console.error('Error loading user like statuses:', error);
      }
    };

    loadUserAndLikeStatuses();
  }, [chat, responses, userId]);

  // Fallback if no chat provided
  if (!chat) {
    return (
      <div style={{
        width: '100%',
        minHeight: '100vh',
        background: '#EDF2F8',
        paddingTop: '80px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
      <div style={{
        background: 'white',
        padding: '40px',
        borderRadius: '12px',
        textAlign: 'center'
      }}>
      <h3>{t('no_chat_selected')}</h3>
      <button onClick={onBack} style={{
        background: theme?.primary || '#024B99',
        color: 'white',
        padding: '12px 24px',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer'
      }}>
      {t('back_to_chat_feed')}
      </button>
      </div>
      </div>
    );
  }

  const handlePostResponse = async (parentChatId) => {
    if (responseText.trim()) {
      if (onPostResponse) {
        try {
          await onPostResponse({
            chatId: chat.id,
            content: responseText.trim(), // as typed (#93)
            parent_chat_id: parentChatId // Response to specific chat
          });
        } catch (error) {
          // Keep what they typed; say why it didn't post (addendum 3 item 8).
          setModal({
            visible: true,
            title: isRateLimited(error) ? t(RATE_LIMIT_TITLE_KEY) : undefined,
            message: rateLimitMessage(error, t) || t('response_post_failed'),
            onOk: () => setModal({ visible: false, message: '', onOk: null })
          });
          return;
        }
      }
      setResponseText('');
      setActiveResponseBox(null);
    }
  };

  const handleResponseClick = (chatId) => {
    setActiveResponseBox(chatId);
    setResponseText('');
  };

  const handleCancelResponse = () => {
    setActiveResponseBox(null);
    setResponseText('');
  };

  // Handle like/dislike click for any message
  const handleLikeDislike = async (messageId, actionType) => {
    if (!currentUserId) {
      setModal({
        visible: true,
        message: t('auth:login_to_like'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    try {
      const prevStatus = userLikeStatuses[messageId];
      console.log(`[ChatDetail] Clicking ${actionType} on message ${messageId.substring(0, 8)}...`, {
        prevStatus,
        currentCount: messageCounts[messageId]
      });

      const result = await userActionService.setMessageLikeDislike(currentUserId, messageId, actionType);

      console.log(`[ChatDetail] Result:`, result);

      if (result.success) {
        const newStatus = result.newStatus;

        // Update like status
        setUserLikeStatuses(prev => ({
          ...prev,
          [messageId]: newStatus
        }));

        // Update counts based on status change
        setMessageCounts(prev => {
          const current = prev[messageId] || { likeCount: 0, dislikeCount: 0 };
          let likeCount = current.likeCount;
          let dislikeCount = current.dislikeCount;

          // Handle like count changes
          if (prevStatus === 'like' && newStatus !== 'like') likeCount = Math.max(0, likeCount - 1);
          if (prevStatus !== 'like' && newStatus === 'like') likeCount++;

          // Handle dislike count changes
          if (prevStatus === 'dislike' && newStatus !== 'dislike') dislikeCount = Math.max(0, dislikeCount - 1);
          if (prevStatus !== 'dislike' && newStatus === 'dislike') dislikeCount++;

          return { ...prev, [messageId]: { likeCount, dislikeCount } };
        });
      } else {
        setModal({
          visible: true,
          message: t('failed_to_save'),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
      }
    } catch (error) {
      console.error('Error saving like/dislike:', error);
      setModal({
        visible: true,
        message: 'Error: ' + error.message,
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    }
  };

  // Calculate engagement score for sorting
  const calculateEngagementScore = (chat) => {
    const likeScore = (chat.like_count || 0) - (chat.dislike_count || 0);
    const ageInHours = (Date.now() - new Date(chat.created_at)) / (1000 * 60 * 60);

    // Time decay: newer content gets boost (24-hour half-life)
    const timeFactor = Math.exp(-ageInHours / 24);

    // Combined score: engagement + freshness
    return likeScore + (timeFactor * 2);
  };

  // Build threaded responses with engagement-based sorting at each level
  const buildSortedThreadedResponses = (responses, parentId = null, depth = 0) => {
    const directResponses = responses.filter(response => response.parent_chat_id === parentId);

    // Sort by engagement score at this level
    const sortedResponses = directResponses.sort((a, b) => {
      return calculateEngagementScore(b) - calculateEngagementScore(a);
    });

    return sortedResponses.map(response => ({
      ...response,
      depth,
      children: buildSortedThreadedResponses(responses, response.id, depth + 1)
    }));
  };

  // Count total descendants in a threaded response tree
  const countDescendants = (chatData) => {
    if (!chatData.children || chatData.children.length === 0) return 0;
    return chatData.children.reduce((sum, child) => sum + 1 + countDescendants(child), 0);
  };

  const renderChatMessage = (chatData) => {
    const { depth = 0 } = chatData;
    const leftBarWidth = depth * 24; // 24px per depth level
    const hasChildren = chatData.children && chatData.children.length > 0;
    const isExpanded = expandedResponses.has(chatData.id);
    const descendantCount = hasChildren ? countDescendants(chatData) : 0;

    return (
      <div key={chatData.id}>
      <div
      onClick={() => hasChildren && toggleExpanded(chatData.id)}
      style={{
        display: 'flex',
        alignItems: 'stretch',
        borderBottom: '1px solid #f0f0f0',
        cursor: hasChildren ? 'pointer' : 'default'
      }}>
      {/* Left threading bar - only show for depth > 0 */}
      {depth > 0 && (
        <div style={{
          width: `${leftBarWidth}px`,
          minHeight: '100%',
          background: '#e5e7eb',
          flexShrink: 0
        }} />
      )}

      {/* Chat content row */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        padding: '4px 16px',
        transition: 'background-color 0.15s'
      }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      >

      {/* Expand/collapse indicator */}
      {hasChildren && (
        <span style={{
          fontSize: '10px',
          color: '#6b7280',
          marginRight: '8px',
          flexShrink: 0,
          transition: 'transform 0.2s',
          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
          display: 'inline-block'
        }}>
          &#9654;
        </span>
      )}

      {/* Content - truncated */}
      <div style={{
        flex: 1,
        fontFamily: 'OpenSauceSans, Arial, sans-serif',
        fontSize: '14px',
        fontWeight: '500',
        color: '#1f2937',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        marginRight: '12px'
      }}>
      {chatData.content}
      </div>

      {/* Collapsed reply count badge */}
      {hasChildren && !isExpanded && (
        <span style={{
          fontFamily: 'OpenSauceSans, Arial, sans-serif',
          fontSize: '11px',
          fontWeight: '600',
          color: '#6b7280',
          backgroundColor: '#f3f4f6',
          borderRadius: '10px',
          padding: '1px 8px',
          marginRight: '8px',
          whiteSpace: 'nowrap',
          flexShrink: 0
        }}>
          {descendantCount} {descendantCount === 1 ? t('reply') : t('replies')}
        </span>
      )}

      {/* Author name - context colored, greyed if removed */}
      <span style={{
        fontFamily: 'OpenSauceSans, Arial, sans-serif',
        fontSize: '13px',
        fontWeight: '600',
        color: chatData.is_author_removed ? '#9CA3AF' : (theme?.primary || '#024B99'),
        marginRight: '12px',
        whiteSpace: 'nowrap',
        fontStyle: chatData.is_author_removed ? 'italic' : 'normal'
      }}>
      {chatData.is_author_removed ? t('common:removed_author') : (chatData.author_name || `User ${chatData.author_id}`)}
      </span>

      {/* Like/Dislike buttons */}
      <span
        onClick={(e) => {
          e.stopPropagation();
          handleLikeDislike(chatData.id, 'like');
        }}
        style={{
          cursor: 'pointer',
          fontSize: '12px',
          opacity: userLikeStatuses[chatData.id] === 'like' ? 1 : 0.5,
          marginRight: '4px'
        }}
      >
        👍
      </span>
      <span style={{ color: '#10B981', fontSize: '11px', marginRight: '8px' }}>
        {messageCounts[chatData.id]?.likeCount || 0}
      </span>
      <span
        onClick={(e) => {
          e.stopPropagation();
          handleLikeDislike(chatData.id, 'dislike');
        }}
        style={{
          cursor: 'pointer',
          fontSize: '12px',
          opacity: userLikeStatuses[chatData.id] === 'dislike' ? 1 : 0.5,
          marginRight: '4px'
        }}
      >
        👎
      </span>
      {(messageCounts[chatData.id]?.dislikeCount || 0) > 0 && (
        <span style={{ color: '#EF4444', fontSize: '11px', marginRight: '12px' }}>
          {messageCounts[chatData.id]?.dislikeCount}
        </span>
      )}

      {/* Report Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (!currentUserId) {
            setModal({
              visible: true,
              message: t('auth:login_required') || 'Please log in to report content',
              onOk: () => setModal({ visible: false, message: '', onOk: null })
            });
            return;
          }
          setReportTargetId(chatData.id);
          setShowReportModal(true);
        }}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '2px 6px',
          marginRight: '8px',
          display: 'inline-flex',
          alignItems: 'center',
          color: theme?.error || '#DC2626',
          fontSize: '11px'
        }}
      >
        <Flag size={14} />
      </button>

      {/* Response Button - green */}
      <button
      onClick={(e) => {
        e.stopPropagation();
        handleResponseClick(chatData.id);
      }}
      style={{
        backgroundColor: '#10B981',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        padding: '3px 12px',
        fontSize: '12px',
        fontWeight: '600',
        cursor: 'pointer',
        fontFamily: 'OpenSauceSans, Arial, sans-serif',
        flexShrink: 0
      }}
      >
      {t('respond')}
      </button>
      </div>
      </div>

      {/* Response Input Box for this specific chat */}
      {activeResponseBox === chatData.id && (
        <div style={{
          marginLeft: depth > 0 ? `${leftBarWidth}px` : '0',
          background: '#f9fafb',
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0'
        }}>
        <textarea
        value={responseText}
        onChange={(e) => setResponseText(e.target.value)}
        onFocus={handleResponseInputFocus}
        placeholder={chatData.is_author_removed ? `${t('common:removed_author')}...` : t('respond_to_placeholder', { name: chatData.author_name || t('this_chat') })}
        style={{
          width: '100%',
          minHeight: '60px',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          padding: '8px',
          fontFamily: 'OpenSauceSans, Arial, sans-serif',
          fontSize: '14px',
          resize: 'vertical',
          outline: 'none'
        }}
        autoFocus
        />

        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
          marginTop: '8px'
        }}>
        <button
        onClick={handleCancelResponse}
        style={{
          backgroundColor: '#e5e7eb',
          color: '#374151',
          border: 'none',
          borderRadius: '6px',
          padding: '3px 14px',
          fontFamily: 'OpenSauceSans, Arial, sans-serif',
          fontSize: '12px',
          fontWeight: '600',
          cursor: 'pointer'
        }}
        >
        {t('common:cancel')}
        </button>

        <button
        onClick={() => handlePostResponse(chatData.id)}
        style={{
          backgroundColor: '#10B981',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          padding: '3px 14px',
          fontFamily: 'OpenSauceSans, Arial, sans-serif',
          fontSize: '12px',
          fontWeight: '600',
          cursor: 'pointer'
        }}
        >
        {t('post')}
        </button>
        </div>
        </div>
      )}

      {/* Render children only when expanded */}
      {isExpanded && chatData.children && chatData.children.map(child =>
        renderChatMessage(child)
      )}
      </div>
    );
  };

  // Get threaded and sorted responses starting from the main chat
  // Start at depth 1 so first-level responses are visually indented under the original chat
  const threadedResponses = buildSortedThreadedResponses(responses, chat.id, 1);

  // Mobile chat message renderer - simplified threading with color bars
  const renderMobileChatMessage = (chatData, isOriginal = false) => {
    const { depth = 0 } = chatData;
    // Mobile: Max visible depth of 3, thin color bars (4px per level)
    const visibleDepth = Math.min(depth, 3);
    const leftPadding = visibleDepth * 12; // 12px indent per level
    const hasChildren = chatData.children && chatData.children.length > 0;
    const isExpanded = isOriginal || expandedResponses.has(chatData.id);
    const descendantCount = hasChildren ? countDescendants(chatData) : 0;

    return (
      <div key={chatData.id} style={{ marginBottom: '8px' }}>
        <div
          onClick={() => !isOriginal && hasChildren && toggleExpanded(chatData.id)}
          style={{
            display: 'flex',
            alignItems: 'stretch'
          }}
        >
          {/* Depth indicator bars */}
          {visibleDepth > 0 && (
            <div style={{
              width: `${leftPadding}px`,
              display: 'flex',
              gap: '4px',
              paddingRight: '8px'
            }}>
              {Array.from({ length: visibleDepth }).map((_, idx) => (
                <div
                  key={idx}
                  style={{
                    width: '4px',
                    backgroundColor: theme?.primary || '#BB4D9A',
                    borderRadius: '2px',
                    opacity: 1 - (idx * 0.2)
                  }}
                />
              ))}
            </div>
          )}

          {/* Chat content card */}
          <div style={{
            flex: 1,
            backgroundColor: isOriginal ? 'white' : '#FAFAFA',
            borderRadius: '12px',
            padding: isOriginal ? '16px' : '12px',
            boxShadow: isOriginal ? '0 2px 8px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.05)',
            cursor: !isOriginal && hasChildren ? 'pointer' : 'default'
          }}>
            {/* Headline for original chat only */}
            {isOriginal && chatData.headline && (
              <h2 style={{
                fontSize: '16px',
                fontWeight: '700',
                color: '#1F2937',
                marginBottom: '8px',
                lineHeight: '1.3'
              }}>
                {chatData.headline}
              </h2>
            )}

            {/* Content */}
            <div style={{
              fontSize: isOriginal ? '14px' : '13px',
              color: '#374151',
              lineHeight: '1.5',
              marginBottom: '8px'
            }}>
              {chatData.content}
            </div>

            {/* Metadata row */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '8px',
              fontSize: '11px',
              color: '#6B7280'
            }}>
              {/* Author and date */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontWeight: '600', color: chatData.is_author_removed ? '#9CA3AF' : (theme?.primary || '#024B99'), fontStyle: chatData.is_author_removed ? 'italic' : 'normal' }}>
                  {chatData.is_author_removed ? t('common:removed_author') : (chatData.author_name || `User ${chatData.author_id}`)}
                </span>
                <span>{new Date(chatData.created_at).toLocaleDateString(formattingLocale)}</span>
              </div>

              {/* Engagement + Like/Dislike + Respond */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* Collapsed reply count badge */}
                {hasChildren && !isOriginal && !isExpanded && (
                  <span style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    color: '#6b7280',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '8px',
                    padding: '1px 6px'
                  }}>
                    {descendantCount} {descendantCount === 1 ? t('reply') : t('replies')}
                  </span>
                )}
                {/* Like/Dislike buttons for all messages */}
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLikeDislike(chatData.id, 'like');
                  }}
                  style={{
                    cursor: 'pointer',
                    fontSize: '16px',
                    opacity: userLikeStatuses[chatData.id] === 'like' ? 1 : 0.5,
                    transition: 'opacity 0.2s ease'
                  }}
                >
                  👍
                </span>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLikeDislike(chatData.id, 'dislike');
                  }}
                  style={{
                    cursor: 'pointer',
                    fontSize: '16px',
                    opacity: userLikeStatuses[chatData.id] === 'dislike' ? 1 : 0.5,
                    transition: 'opacity 0.2s ease'
                  }}
                >
                  👎
                </span>
                {/* Like/Dislike counts */}
                <span style={{ color: '#10B981', fontSize: '11px' }}>
                  {messageCounts[chatData.id]?.likeCount || 0}
                </span>
                {(messageCounts[chatData.id]?.dislikeCount || 0) > 0 && (
                  <span style={{ color: '#EF4444', fontSize: '11px' }}>
                    {messageCounts[chatData.id]?.dislikeCount}
                  </span>
                )}
                {/* Report Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!currentUserId) {
                      setModal({
                        visible: true,
                        message: t('auth:login_required') || 'Please log in to report content',
                        onOk: () => setModal({ visible: false, message: '', onOk: null })
                      });
                      return;
                    }
                    setReportTargetId(chatData.id);
                    setShowReportModal(true);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    marginLeft: '8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: theme?.error || '#DC2626'
                  }}
                >
                  <Flag size={16} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResponseClick(chatData.id);
                  }}
                  style={{
                    background: theme?.accent || '#4FBFAE',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '4px 10px',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {t('respond')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Response Input Box */}
        {activeResponseBox === chatData.id && (
          <div style={{
            marginLeft: `${leftPadding}px`,
            marginTop: '8px',
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '12px',
            border: '2px solid #E5E7EB'
          }}>
            <textarea
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              onFocus={handleResponseInputFocus}
              placeholder={chatData.is_author_removed ? `${t('common:removed_author')}...` : t('respond_to_placeholder', { name: chatData.author_name || t('this_chat') })}
              style={{
                width: '100%',
                minHeight: '60px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                padding: '8px',
                fontSize: '13px',
                resize: 'vertical',
                outline: 'none'
              }}
              autoFocus
            />
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '8px'
            }}>
              <button
                onClick={handleCancelResponse}
                style={{
                  background: '#6B7280',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 14px',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={() => handlePostResponse(chatData.id)}
                style={{
                  background: theme?.accent || '#4FBFAE',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 14px',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('post')}
              </button>
            </div>
          </div>
        )}

        {/* Render children only when expanded */}
        {isExpanded && chatData.children && chatData.children.map(child =>
          renderMobileChatMessage(child, false)
        )}
      </div>
    );
  };

  // Mobile Layout
  if (isMobile) {
    return (
      <>
        {/* Header - Layered architecture */}
        <HeaderContainer />
        <HeaderButtonsForm
          currentContext={currentContext}
          onBack={onBack}
          headerTitle={chat.headline || 'Chat'}
        />

        <div style={{
          width: '100%',
          minHeight: '100vh',
          backgroundColor: '#EDF2F8',
          paddingTop: `${headerHeight}px`,
          paddingBottom: `${footerHeight}px`
        }}>

        {/* Main Content - Scrollable */}
        <div style={{
          padding: '16px',
          overflowY: 'auto',
          height: `calc(100vh - ${headerHeight + footerHeight}px)`
        }}>
          {/* Original Chat */}
          {renderMobileChatMessage({ ...chat, depth: 0 }, true)}

          {/* Responses Section */}
          {threadedResponses.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#1F2937',
                marginBottom: '12px'
              }}>
                {t('responses')} ({responses.length})
              </h3>
              {threadedResponses.map(response => renderMobileChatMessage(response, false))}
            </div>
          )}

          {/* No responses message */}
          {threadedResponses.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '24px',
              color: '#6B7280',
              fontSize: '13px'
            }}>
              {t('no_responses_yet')}
            </div>
          )}
        </div>

        {/* Mobile Footer */}
        <FooterContainer viewType="detail" isMobile={true} />

        {/* Confirmation Modal */}
        <ConfirmationModal
          visible={modal.visible}
          title={modal.title || t('notification')}
          message={modal.message}
          onOk={() => {
            try {
              modal.onOk && modal.onOk();
            } catch (e) {
              console.error(e);
              setModal({ visible: false, message: '', onOk: null });
            }
          }}
          onCancel={() => {
            setModal({ visible: false, message: '', onOk: null });
          }}
        />
        </div>
      </>
    );
  }

  // Desktop Layout
  return (
    <div style={{
      width: '100%',
      minHeight: '100vh',
      backgroundColor: '#EDF2F8'
    }}>
    {/* Chat Detail Header */}
    <HeaderContainer />
    <HeaderButtonsForm
      currentContext={currentContext}
      onBack={onBack}
      headerTitle={chat.headline || 'Chat'}
    />

    {/* Content area - positioned below header */}
    <div style={{
      paddingTop: '80px',
      paddingBottom: '140px',
      position: 'relative'
    }}>
    {/* White container - 750px wide, matching ChatGrid positioning */}
    <div style={{
      position: 'absolute',
      top: '80px',
      left: '496px',
      width: '750px',
      maxWidth: 'calc(100% - 520px)',
      maxHeight: 'calc(100vh - 200px)',
      backgroundColor: 'white',
      borderRadius: '12px',
      overflow: 'auto',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
    }}>

    {/* Content area */}
    <div>

    {/* Original Chat Message - compact header row */}
    <div style={{
      display: 'flex',
      alignItems: 'center',
      padding: '8px 16px',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb'
    }}>

    {/* Headline */}
    <div style={{
      flex: 1,
      fontFamily: 'OpenSauceSans, Arial, sans-serif',
      fontSize: '14px',
      fontWeight: '600',
      color: '#1f2937',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      marginRight: '12px'
    }}>
    {chat.headline || chat.content}
    </div>

    {/* Author name - context colored, greyed if removed */}
    <span style={{
      fontFamily: 'OpenSauceSans, Arial, sans-serif',
      fontSize: '13px',
      fontWeight: '600',
      color: chat.is_author_removed ? '#9CA3AF' : (theme?.primary || '#024B99'),
      marginRight: '12px',
      whiteSpace: 'nowrap',
      fontStyle: chat.is_author_removed ? 'italic' : 'normal'
    }}>
    {chat.is_author_removed ? t('common:removed_author') : (chat.author_name || `User ${chat.author_id}`)}
    </span>

    {/* Like/Dislike */}
    <span
      onClick={() => handleLikeDislike(chat.id, 'like')}
      style={{
        cursor: 'pointer',
        fontSize: '12px',
        opacity: userLikeStatuses[chat.id] === 'like' ? 1 : 0.5,
        marginRight: '4px'
      }}
    >
      👍
    </span>
    <span style={{ color: '#10B981', fontSize: '11px', marginRight: '8px' }}>
      {messageCounts[chat.id]?.likeCount || 0}
    </span>
    <span
      onClick={() => handleLikeDislike(chat.id, 'dislike')}
      style={{
        cursor: 'pointer',
        fontSize: '12px',
        opacity: userLikeStatuses[chat.id] === 'dislike' ? 1 : 0.5,
        marginRight: '4px'
      }}
    >
      👎
    </span>
    {(messageCounts[chat.id]?.dislikeCount || 0) > 0 && (
      <span style={{ color: '#EF4444', fontSize: '11px', marginRight: '12px' }}>
        {messageCounts[chat.id]?.dislikeCount}
      </span>
    )}

    {/* Report Button */}
    <button
      onClick={() => {
        if (!currentUserId) {
          setModal({
            visible: true,
            message: t('auth:login_required') || 'Please log in to report content',
            onOk: () => setModal({ visible: false, message: '', onOk: null })
          });
          return;
        }
        setReportTargetId(chat.id);
        setShowReportModal(true);
      }}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '2px 6px',
        marginRight: '8px',
        display: 'inline-flex',
        alignItems: 'center',
        color: theme?.error || '#DC2626',
        fontSize: '11px'
      }}
    >
      <Flag size={14} />
    </button>

    {/* Respond Button - green */}
    <button
    onClick={() => handleResponseClick(chat.id)}
    style={{
      backgroundColor: '#10B981',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      padding: '3px 14px',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      fontFamily: 'OpenSauceSans, Arial, sans-serif',
      flexShrink: 0
    }}
    >
    {t('respond')}
    </button>
    </div>

    {/* Original chat full content */}
    <div style={{
      padding: '12px 16px',
      borderBottom: '1px solid #f0f0f0',
      fontFamily: 'OpenSauceSans, Arial, sans-serif',
      fontSize: '14px',
      lineHeight: '1.5',
      color: '#374151'
    }}>
    {chat.content}
    </div>

    {/* Response Input Box for original chat */}
    {activeResponseBox === chat.id && (
      <div style={{
        background: '#f9fafb',
        padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0'
      }}>
      <textarea
      value={responseText}
      onChange={(e) => setResponseText(e.target.value)}
      onFocus={handleResponseInputFocus}
      placeholder={t('respond_placeholder')}
      style={{
        width: '100%',
        minHeight: '60px',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        padding: '8px',
        fontFamily: 'OpenSauceSans, Arial, sans-serif',
        fontSize: '14px',
        resize: 'vertical',
        outline: 'none'
      }}
      autoFocus
      />

      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
        marginTop: '8px'
      }}>
      <button
      onClick={handleCancelResponse}
      style={{
        backgroundColor: '#e5e7eb',
        color: '#374151',
        border: 'none',
        borderRadius: '6px',
        padding: '3px 14px',
        fontFamily: 'OpenSauceSans, Arial, sans-serif',
        fontSize: '12px',
        fontWeight: '600',
        cursor: 'pointer'
      }}
      >
      {t('common:cancel')}
      </button>

      <button
      onClick={() => handlePostResponse(chat.id)}
      style={{
        backgroundColor: '#10B981',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        padding: '3px 14px',
        fontFamily: 'OpenSauceSans, Arial, sans-serif',
        fontSize: '12px',
        fontWeight: '600',
        cursor: 'pointer'
      }}
      >
      {t('post')}
      </button>
      </div>
      </div>
    )}

    {/* Threaded Response Section */}
    {threadedResponses.length > 0 && (
      <>
      <div style={{
        display: 'flex',
        alignItems: 'stretch',
        borderBottom: '1px solid #e5e7eb'
      }}>
      {/* Indent bar matching depth-1 responses */}
      <div style={{
        width: '24px',
        background: '#e5e7eb',
        flexShrink: 0
      }} />
      <div style={{
        flex: 1,
        padding: '6px 16px',
        backgroundColor: '#f9fafb',
        fontFamily: 'OpenSauceSans, Arial, sans-serif',
        fontSize: '12px',
        fontWeight: '600',
        color: '#6b7280'
      }}>
      {t('responses')} ({responses.length})
      </div>
      </div>

      {threadedResponses.map(response => renderChatMessage(response))}
      </>
    )}

    {/* No responses message */}
    {threadedResponses.length === 0 && (
      <div style={{
        textAlign: 'center',
        padding: '24px',
        color: '#6b7280',
        fontSize: '13px'
      }}>
        {t('no_responses_yet_detail')}
      </div>
    )}
    </div>
    </div>
    </div>

    {/* Confirmation Modal */}
    <ConfirmationModal
      visible={modal.visible}
      title={t('notification')}
      message={modal.message}
      onOk={() => {
        try {
          modal.onOk && modal.onOk();
        } catch (e) {
          console.error(e);
          setModal({ visible: false, message: '', onOk: null });
        }
      }}
      onCancel={() => {
        setModal({ visible: false, message: '', onOk: null });
      }}
    />

    {/* Report Chat Modal */}
    <ReportModal
      visible={showReportModal}
      contentType="chat"
      contentId={reportTargetId}
      onClose={() => {
        setShowReportModal(false);
        setReportTargetId(null);
      }}
      onSubmit={async (reportData) => {
        const result = await userActionService.submitReport(currentUserId, reportData);
        if (result.success) {
          setModal({
            visible: true,
            message: t('common:report_submitted') || 'Report submitted. Thank you.',
            onOk: () => setModal({ visible: false, message: '', onOk: null })
          });
          setShowReportModal(false);
          setReportTargetId(null);
        }
      }}
    />
    </div>
  );
};

export default ChatDetail;
